// A curated pool of warm, team-focused daily messages.
// Every visit on the same calendar day shows the same message — and it rotates
// once at midnight. No LLM calls; no network; zero latency on the login page.
// Tone: written AS management, addressed directly TO the team. First person
// plural ("we"), second person ("you"), never "users" or "employees".

const MESSAGES = [
  {
    title: "Welcome home, team.",
    body: "Every clean room, every warm smile, every quiet fix — that's you, making someone's holiday unforgettable.",
  },
  {
    title: "We're so glad you're here.",
    body: "Guests will remember three things from today — a kind welcome, a tidy room, a problem you solved. Thank you in advance.",
  },
  {
    title: "Good to see you today.",
    body: "You don't just run a property. You turn an ordinary day into someone's favourite memory. That matters.",
  },
  {
    title: "Take a breath — then begin.",
    body: "There's no rush. Care, not speed, is what brings our guests back. Move at your pace, and we've got your back.",
  },
  {
    title: "Your hands make magic.",
    body: "A folded towel, a fixed tap, a cheerful 'good morning' — small things, big feelings. The team here is lucky to have you.",
  },
  {
    title: "Big day, small steps.",
    body: "Pick the first task. Finish it well. Then the next. We don't need superheroes — we need you, present and steady.",
  },
  {
    title: "You set the tone today.",
    body: "Guests don't read our brand book. They feel the team's mood. Bring kindness with you — the rest takes care of itself.",
  },
  {
    title: "Thank you for showing up.",
    body: "Some days are heavier than others. Whichever today is for you, we see you — and we're proud you're on our side.",
  },
  {
    title: "Today is page one.",
    body: "Whatever happened yesterday, today is a clean sheet. Make it a good story for at least one guest.",
  },
  {
    title: "Small things, repeated daily.",
    body: "That's what excellence actually looks like. You don't need a perfect day — just one honest one. Let's go.",
  },
  {
    title: "We're a team. Lean on us.",
    body: "If something's broken — a room, a process, or even your own day — flag it. We'd rather know now than fix it later.",
  },
  {
    title: "You are the reason they return.",
    body: "Repeat guests don't book the brochure. They book you, because of how you made them feel last time.",
  },
  {
    title: "Be the warmest part of their stay.",
    body: "Cool weather, cool linens, warm welcome. The third one is your superpower — and only yours.",
  },
  {
    title: "Slow is smooth. Smooth is fast.",
    body: "Don't sprint at the start. Pace yourself across the day, and you'll finish stronger and kinder than rushing ever made anyone.",
  },
  {
    title: "Show up as yourself.",
    body: "Guests come for the property, but stay loyal to the people. The person who turns up today is exactly who we need.",
  },
  {
    title: "Every detail is the detail.",
    body: "One fewer crease in the sheet, one straighter cushion, one warmer 'see you tomorrow.' That's the whole job. You're great at it.",
  },
  {
    title: "Quiet wins count too.",
    body: "Not every good day comes with applause. Some of your best work today will be invisible — and we'll still know.",
  },
  {
    title: "We trust you completely.",
    body: "You're not here to ask permission for kindness. If a guest needs something small, just do it. We'll always back the call.",
  },
  {
    title: "Look out for each other.",
    body: "If a teammate looks tired, ask. If a guest looks lost, walk them. Tiny acts of care, multiplied by the team. That's us.",
  },
  {
    title: "Be patient with yourself.",
    body: "You'll have a not-perfect moment today. So will we. Forgive it quickly and keep going — that's the difference between good and great.",
  },
  {
    title: "Your work travels home with them.",
    body: "Guests will go back and tell three people about us this month. Most of those stories start with something you did.",
  },
  {
    title: "Pace, pause, push.",
    body: "Start steady, take your break, finish what matters. Anything left over, we'll pick up tomorrow. Promise.",
  },
  {
    title: "You belong here.",
    body: "However long you've been with us, today you're part of the team. We're not complete without you. Welcome back.",
  },
  {
    title: "Make one guest's day unforgettable.",
    body: "Pick a small thing — a fresh flower, a hand-written note, an extra minute of attention. Watch what happens.",
  },
  {
    title: "Today, take ownership.",
    body: "If you see it, fix it — or flag it. There's no 'not my job' here. The property is ours, together.",
  },
  {
    title: "Lead with care.",
    body: "Standards are not what we follow when watched — they're what we keep when no one's looking. We know who you are.",
  },
  {
    title: "Have a cup of tea first.",
    body: "Seriously. Breathe. Plan your day. Then begin. We'd rather have a calm you for ten hours than a frantic you for three.",
  },
  {
    title: "Be the welcome, not the rule.",
    body: "Policies can wait. A smile and 'how can I help?' goes further than any signage we'll ever print.",
  },
  {
    title: "Today, ask for help.",
    body: "Stuck somewhere? Tired of one task? Tell us. We don't reward silent struggle — we reward honest teamwork.",
  },
  {
    title: "Your effort compounds.",
    body: "One careful day x 300 days = a property that runs on autopilot. You're building something bigger than today.",
  },
  {
    title: "Be gentle with the new ones.",
    body: "Someone on the team is doing this for the first time today. Show them how. The whole team gets stronger when you do.",
  },
  {
    title: "Small kindness, big tip.",
    body: "Not just the money kind — the kind that makes someone walk a little lighter all day. You're really good at that.",
  },
  {
    title: "Stay curious.",
    body: "Where can it be a little better? A faster checklist, a tidier closet, a kinder phrase. Tell us — we listen.",
  },
  {
    title: "We're rooting for you.",
    body: "Not just at the front desk, not just in housekeeping, not just in maintenance — all of you, all day. Go gentle, finish strong.",
  },
  {
    title: "Make today useful.",
    body: "You don't need to make it perfect. Just one room cleaner, one ticket closed, one teammate cheered. That's the bar.",
  },
  {
    title: "Bring your laugh.",
    body: "Our team's energy is contagious. Bring yours — guests pick it up before they even unpack.",
  },
  {
    title: "Whatever's heavy, set it down.",
    body: "Leave outside what doesn't belong inside. Bring in your craft, your warmth, your wit. The rest can wait.",
  },
  {
    title: "Notice one beautiful thing.",
    body: "The light through the lobby, a guest's laugh, a perfectly made bed. Beauty makes the day longer in the right way.",
  },
  {
    title: "You're more important than the metric.",
    body: "We care about KPIs, sure. We care about you more. If today is hard, tell us — the dashboard can wait.",
  },
  {
    title: "Be the calm in their day.",
    body: "Guests arrive with the noise of travel. Send them off with the quiet of being looked after. You do that beautifully.",
  },
];

/**
 * Deterministic message-of-the-day — everyone sees the same message on the
 * same calendar day. Rotates at local midnight. No API call, no LLM cost.
 */
export function getDailyMessage(date = new Date()) {
  const y = date.getFullYear();
  const start = new Date(y, 0, 0);
  const diff = date - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  // Mix year so the same Jan-1 doesn't repeat next year
  const idx = (dayOfYear + y * 7) % MESSAGES.length;
  return MESSAGES[idx];
}

export function getGreeting(date = new Date()) {
  const h = date.getHours();
  if (h < 5) return "Working late — take care of yourself";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  if (h < 21) return "Good evening";
  return "Working into the night — we see you";
}
